import React, { useMemo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

function CalendarView({ 
  data, 
  selectedOrganisations, 
  yearFilter, 
  typeFilter, 
  verifiedFilter, 
  relevanceFilter = 0, 
  locationFilter,
  costFilter,
  registrationDeadlineFilter,
  submissionDeadlineFilter,
  memberSizeFilter,
  onReportIssue,
  selectedYear,
  selectedMonth,
  onMonthChange,
  onDateChange,
  onConferenceClick
}) {
  const currentDate = new Date()

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const dayNames = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
  const dayNamesFull = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay()
    return firstDay === 0 ? 6 : firstDay - 1 // Convert Sunday (0) to 6, shift others
  }

  const getLastDayOfMonth = (year, month) => {
    const daysInMonth = getDaysInMonth(year, month)
    const lastDay = new Date(year, month, daysInMonth).getDay()
    return lastDay === 0 ? 6 : lastDay - 1 // Convert Sunday (0) to 6, shift others
  }



  const filteredConferences = useMemo(() => {
    if (!data?.conferences) return []

    return data.conferences.filter(conf => {
      // Organisation filter
      if (!selectedOrganisations.has(conf.organisationId)) return false

      // Year filter (multi-select)
      if (yearFilter.size > 0) {
        const confYear = conf.year ? conf.year.toString() : null
        const hasValidYear = confYear && yearFilter.has(confYear)
        const hasNotSpecified = !confYear && yearFilter.has('not_specified')
        if (!hasValidYear && !hasNotSpecified) return false
      }

      // Type filter (check organisation type) (multi-select)
      if (typeFilter.size > 0) {
        const org = data.organisations?.find(o => o.id === conf.organisationId)
        const orgType = org?.type || null
        const hasValidType = orgType && typeFilter.has(orgType)
        const hasNotSpecified = !orgType && typeFilter.has('not_specified')
        if (!hasValidType && !hasNotSpecified) return false
      }

      // Member size filter (check organisation size) (multi-select)
      if (memberSizeFilter.size > 0) {
        const org = data.organisations?.find(o => o.id === conf.organisationId)
        const memberCount = org?.memberCount
        let sizeCategory = null
        
        if (memberCount) {
          if (memberCount >= 1 && memberCount <= 99) sizeCategory = 'small'
          else if (memberCount >= 100 && memberCount <= 999) sizeCategory = 'medium'
          else if (memberCount >= 1000) sizeCategory = 'large'
        }
        
        const hasValidSize = sizeCategory && memberSizeFilter.has(sizeCategory)
        const hasNotSpecified = !memberCount && memberSizeFilter.has('not_specified')
        if (!hasValidSize && !hasNotSpecified) return false
      }

      // Location filter (multi-select)
      if (locationFilter.size > 0) {
        const location = conf.location?.toLowerCase() || ''
        let locationCategory = null
        
        if (location) {
          if (location.includes('auckland')) locationCategory = 'auckland'
          else if (location.includes('wellington')) locationCategory = 'wellington'
          else if (location.includes('christchurch')) locationCategory = 'christchurch'
          else if (location.includes('new zealand') || location.includes('nz') || 
                   location.includes('hamilton') || location.includes('dunedin') || 
                   location.includes('tauranga') || location.includes('rotorua')) locationCategory = 'other_nz'
          else if (location.includes('australia') || location.includes('sydney') || 
                   location.includes('melbourne') || location.includes('brisbane')) locationCategory = 'australia'
          else locationCategory = 'international'
        }
        
        const hasValidLocation = locationCategory && locationFilter.has(locationCategory)
        const hasNotSpecified = !location && locationFilter.has('not_specified')
        if (!hasValidLocation && !hasNotSpecified) return false
      }

      // Cost filter (multi-select)
      if (costFilter.size > 0) {
        const cost = conf.registrationCost
        let costCategory = null
        
        if (cost) {
          if (typeof cost === 'string') {
            const costLower = cost.toLowerCase()
            if (costLower.includes('free') || costLower.includes('$0') || costLower === '0') {
              costCategory = 'free'
            } else {
              // Parse cost ranges - simplified logic
              const match = costLower.match(/\$?(\d+)/)
              if (match) {
                const amount = parseInt(match[1])
                if (amount >= 1 && amount <= 499) costCategory = 'low'
                else if (amount >= 500 && amount <= 1499) costCategory = 'medium'
                else if (amount >= 1500) costCategory = 'high'
              }
            }
          }
        }
        
        const hasValidCost = costCategory && costFilter.has(costCategory)
        const hasNotSpecified = !cost && costFilter.has('not_specified')
        if (!hasValidCost && !hasNotSpecified) return false
      }

      // Registration deadline filter (multi-select)
      if (registrationDeadlineFilter.size > 0) {
        const deadline = conf.registrationDeadline
        let deadlineCategory = null
        
        if (deadline) {
          const deadlineDate = new Date(deadline)
          const now = new Date()
          const daysUntil = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24))
          
          if (daysUntil < 0) deadlineCategory = 'overdue'
          else if (daysUntil >= 1 && daysUntil <= 7) deadlineCategory = 'soon'
          else if (daysUntil >= 8 && daysUntil <= 30) deadlineCategory = 'upcoming'
          else if (daysUntil > 30) deadlineCategory = 'future'
        }
        
        const hasValidDeadline = deadlineCategory && registrationDeadlineFilter.has(deadlineCategory)
        const hasNotSpecified = !deadline && registrationDeadlineFilter.has('not_specified')
        if (!hasValidDeadline && !hasNotSpecified) return false
      }

      // Submission deadline filter (multi-select)
      if (submissionDeadlineFilter.size > 0) {
        const deadline = conf.paperSubmissionDeadline
        let deadlineCategory = null
        
        if (deadline) {
          const deadlineDate = new Date(deadline)
          const now = new Date()
          const daysUntil = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24))
          
          if (daysUntil < 0) deadlineCategory = 'closed'
          else if (daysUntil >= 1 && daysUntil <= 7) deadlineCategory = 'closing_soon'
          else if (daysUntil >= 8 && daysUntil <= 30) deadlineCategory = 'open_soon'
          else if (daysUntil > 30) deadlineCategory = 'open_future'
        }
        
        const hasValidDeadline = deadlineCategory && submissionDeadlineFilter.has(deadlineCategory)
        const hasNotSpecified = !deadline && submissionDeadlineFilter.has('not_specified')
        if (!hasValidDeadline && !hasNotSpecified) return false
      }

      // Verification filter (multi-select)
      if (verifiedFilter.size > 0) {
        const isVerified = conf.verified === true
        const hasVerified = isVerified && verifiedFilter.has('verified')
        const hasUnverified = !isVerified && verifiedFilter.has('unverified')
        if (!hasVerified && !hasUnverified) return false
      }

      // Relevance filter (still a range slider)
      const relevance = conf.relevancePercentage || 0
      if (relevance < relevanceFilter) return false

      return true
    })
  }, [data, selectedOrganisations, yearFilter, typeFilter, verifiedFilter, relevanceFilter, locationFilter, costFilter, registrationDeadlineFilter, submissionDeadlineFilter, memberSizeFilter])

  // Generate consistent color based on conference name
  const getConferenceColor = (conferenceName) => {
    if (!conferenceName) return '#3b82f6'
    
    let hash = 0
    for (let i = 0; i < conferenceName.length; i++) {
      const char = conferenceName.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }
    
    const colors = [
      '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
      '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1',
      '#059669', '#dc2626', '#7c3aed', '#0891b2', '#65a30d',
      '#ea580c', '#db2777', '#4f46e5', '#0d9488', '#b91c1c'
    ]
    
    return colors[Math.abs(hash) % colors.length]
  }

  const getGridPosition = (day) => {
    const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth)
    return (day - 1 + firstDay)
  }
  
  const findConsecutiveSpans = (monthDays) => {
    if (monthDays.length === 0) return []
    if (monthDays.length === 1) return [monthDays]
    
    const spans = []
    let currentSpan = [monthDays[0]]
    
    for (let i = 1; i < monthDays.length; i++) {
      const prev = monthDays[i-1]
      const current = monthDays[i]
      
      // Check if consecutive and on same row
      const prevRow = Math.floor(prev.gridPosition / 7)
      const currentRow = Math.floor(current.gridPosition / 7)
      const isConsecutive = current.day === prev.day + 1
      const sameRow = prevRow === currentRow
      
      if (isConsecutive && sameRow) {
        currentSpan.push(current)
      } else {
        spans.push(currentSpan)
        currentSpan = [current]
      }
    }
    
    spans.push(currentSpan)
    return spans
  }

  // Generate calendar layout with collision detection and track assignment
  const calendarLayout = useMemo(() => {
    const allEvents = []
    
    // First, collect all conferences and their date ranges
    filteredConferences.forEach(conf => {
      if (conf.dates && conf.dates.length > 0) {
        const allDates = []
        
        if (conf.dates.length === 1) {
          allDates.push(new Date(conf.dates[0]))
        } else if (conf.dates.length === 2) {
          const startDate = new Date(conf.dates[0])
          const endDate = new Date(conf.dates[1])
          
          let currentDate = new Date(startDate)
          while (currentDate <= endDate) {
            allDates.push(new Date(currentDate))
            currentDate.setDate(currentDate.getDate() + 1)
          }
        } else {
          conf.dates.forEach(dateStr => allDates.push(new Date(dateStr)))
        }
        
        // Filter for current month and convert to day numbers
        const monthDays = allDates
          .filter(date => date.getFullYear() === selectedYear && date.getMonth() === selectedMonth)
          .map(date => date.getDate())
          .sort((a, b) => a - b)
        
        if (monthDays.length === 0) return
        
        // Create event entry
        allEvents.push({
          conference: conf,
          startDay: monthDays[0],
          endDay: monthDays[monthDays.length - 1],
          days: monthDays,
          duration: monthDays.length
        })
      }
    })
    
    // Sort events by start day, then by duration (longer events first for better packing)
    allEvents.sort((a, b) => {
      if (a.startDay !== b.startDay) return a.startDay - b.startDay
      return b.duration - a.duration
    })
    
    // Assign tracks (vertical levels) using collision detection
    const tracks = []  // Each track contains events that don't overlap
    const eventsWithTracks = []
    
    allEvents.forEach(event => {
      // Find the first track where this event doesn't collide
      let assignedTrack = -1
      
      for (let trackIndex = 0; trackIndex < tracks.length; trackIndex++) {
        const track = tracks[trackIndex]
        let hasCollision = false
        
        // Check if this event collides with any event in this track
        for (const existingEvent of track) {
          if (!(event.endDay < existingEvent.startDay || event.startDay > existingEvent.endDay)) {
            hasCollision = true
            break
          }
        }
        
        if (!hasCollision) {
          assignedTrack = trackIndex
          break
        }
      }
      
      // If no existing track works, create a new one
      if (assignedTrack === -1) {
        assignedTrack = tracks.length
        tracks.push([])
      }
      
      // Add event to the track
      tracks[assignedTrack].push(event)
      eventsWithTracks.push({
        ...event,
        track: assignedTrack
      })
    })
    
    return { events: eventsWithTracks, maxTracks: tracks.length }
  }, [filteredConferences, selectedYear, selectedMonth])


  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth)
    const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth)
    const lastDay = getLastDayOfMonth(selectedYear, selectedMonth)
    const days = []

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null)
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day)
    }

    // Add empty cells for days after the last day of the month to complete the week
    // If lastDay is 6 (Sunday), no empty cells needed. Otherwise, add cells until Sunday.
    const emptyCellsAtEnd = lastDay === 6 ? 0 : 6 - lastDay
    for (let i = 0; i < emptyCellsAtEnd; i++) {
      days.push(null)
    }

    return days
  }, [selectedYear, selectedMonth, getLastDayOfMonth])

  const handleMonthNavigation = (direction) => {
    // Get available years from conference data
    const availableYears = data?.conferences
      ? Array.from(new Set(data.conferences.map(conf => conf.year).filter(Boolean))).sort((a, b) => a - b)
      : [selectedYear]
    
    let newMonth = selectedMonth
    let newYear = selectedYear
    
    if (direction === -1) {
      // Going backwards
      if (selectedMonth === 0) {
        newYear = selectedYear - 1
        newMonth = 11
      } else {
        newMonth = selectedMonth - 1
      }
    } else {
      // Going forwards
      if (selectedMonth === 11) {
        newYear = selectedYear + 1
        newMonth = 0
      } else {
        newMonth = selectedMonth + 1
      }
    }
    
    // Only apply changes if the target year is within available data range
    if (availableYears.includes(newYear)) {
      if (newYear !== selectedYear) {
        // When crossing year boundaries, use atomic date change to prevent race conditions
        onDateChange(newYear, newMonth)
      } else {
        // Same year, just change month
        onMonthChange(newMonth)
      }
    }
    // If year is out of range, do nothing (prevents navigation)
  }

  const handleTodayClick = () => {
    const todayYear = currentDate.getFullYear()
    const todayMonth = currentDate.getMonth()
    
    // Get available years from conference data
    const availableYears = data?.conferences
      ? Array.from(new Set(data.conferences.map(conf => conf.year).filter(Boolean))).sort((a, b) => a - b)
      : [todayYear]
    
    // Use today's date if it's within the data range, otherwise use the closest available year
    let targetYear = todayYear
    if (!availableYears.includes(todayYear)) {
      // Find the closest available year to today
      targetYear = availableYears.reduce((closest, year) => 
        Math.abs(year - todayYear) < Math.abs(closest - todayYear) ? year : closest
      )
    }
    
    // Use atomic date change to prevent race conditions
    onDateChange(targetYear, todayMonth)
  }

  const handleConferenceClick = (conference) => {
    onConferenceClick(conference)
  }


  return (
    <div className="calendar-view">
      <div className="main-calendar">
        <div className="calendar-header">
          <button className="today-btn" onClick={handleTodayClick}>
            Today
          </button>
          <div className="month-navigation">
            <button onClick={() => handleMonthNavigation(-1)} className="month-nav-btn">
              <ChevronLeft size={20} />
            </button>
            <button onClick={() => handleMonthNavigation(1)} className="month-nav-btn">
              <ChevronRight size={20} />
            </button>
            <h3>{monthNames[selectedMonth]} {selectedYear}</h3>
          </div>
        </div>

        <div className="calendar-grid">
          <div className="day-headers">
            {dayNames.map((day, index) => (
              <div key={dayNamesFull[index]} className="day-header">{day}</div>
            ))}
          </div>

          <div className="calendar-body">
            {/* Render date cells */}
            {calendarDays.map((day, index) => {
              const row = Math.floor(index / 7) + 1
              const col = (index % 7) + 1
              
              return (
                <div 
                  key={index} 
                  className={`date-cell ${!day ? 'empty' : ''}`}
                  style={{
                    gridRow: row,
                    gridColumn: col
                  }}
                >
                  {day && (
                    <>
                      <div className="date-number">{day}</div>
                    </>
                  )}
                </div>
              )
            })}
            
            {/* Render all events (single and multi-day) in the same grid */}
            {calendarLayout.events.map((event, index) => {
              const { conference, track } = event
              
              // Find consecutive spans for this event
              const consecutiveSpans = findConsecutiveSpans(
                event.days.map(day => ({
                  day,
                  gridPosition: getGridPosition(day)
                }))
              )
              
              return consecutiveSpans.map((span, spanIndex) => {
                const startPosition = span[0].gridPosition
                const spanLength = span.length
                const row = Math.floor(startPosition / 7) + 1
                const col = (startPosition % 7) + 1
                
                return (
                  <div
                    key={`${conference.id}-${index}-${spanIndex}`}
                    className="conference-event"
                    style={{
                      backgroundColor: getConferenceColor(conference.name),
                      gridRow: row,
                      gridColumn: spanLength > 1 ? `${col} / span ${spanLength}` : col,
                      marginTop: `${1.75 + track * 1.5}rem`,
                      zIndex: 10 + track
                    }}
                    onClick={() => handleConferenceClick(conference)}
                    title={conference.name}
                  >
                    {conference.name}
                  </div>
                )
              })
            })}
          </div>
        </div>
      </div>

    </div>
  )
}

export default CalendarView